/**
 * Razorpay Integration Hook
 * 
 * Handles loading Razorpay script and opening checkout
 */
import { useState, useCallback, useEffect } from 'react';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Hook for Razorpay payment integration
 * @returns {Object} - { isLoaded, isLoading, error, openCheckout }
 */
export function useRazorpay() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if Razorpay is already loaded
  useEffect(() => {
    if (window.Razorpay) {
      setIsLoaded(true);
    }
  }, []);

  /**
   * Load Razorpay script dynamically
   */
  const loadScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      // Already loaded
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(true));
        existingScript.addEventListener('error', () => reject(new Error('Failed to load Razorpay')));
        return;
      }

      // Create and load script
      const script = document.createElement('script');
      script.src = RAZORPAY_SCRIPT_URL;
      script.async = true;
      
      script.onload = () => {
        setIsLoaded(true);
        setIsLoading(false);
        resolve(true);
      };
      
      script.onerror = () => {
        setError('Failed to load payment gateway');
        setIsLoading(false);
        reject(new Error('Failed to load Razorpay'));
      };

      setIsLoading(true);
      document.body.appendChild(script);
    });
  }, []);

  /**
   * Open Razorpay checkout
   * @param {Object} options - Razorpay checkout options
   * @returns {Promise} - Resolves with payment response or rejects with error
   */
  const openCheckout = useCallback(async (options) => {
    try {
      // Ensure script is loaded
      if (!window.Razorpay) {
        await loadScript();
      }

      return new Promise((resolve, reject) => {
        const razorpayOptions = {
          ...options,
          handler: function (response) {
            resolve({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
          },
          modal: {
            ondismiss: function () {
              reject({ dismissed: true, message: 'Payment cancelled by user' });
            },
            escape: true,
            animation: true,
          },
        };

        const razorpay = new window.Razorpay(razorpayOptions);
        
        razorpay.on('payment.failed', function (response) {
          reject({
            code: response.error.code,
            description: response.error.description,
            source: response.error.source,
            step: response.error.step,
            reason: response.error.reason,
            metadata: response.error.metadata,
          });
        });

        razorpay.open();
      });
    } catch (err) {
      setError(err.message || 'Failed to open payment gateway');
      throw err;
    }
  }, [loadScript]);

  return {
    isLoaded,
    isLoading,
    error,
    loadScript,
    openCheckout,
  };
}

export default useRazorpay;
